import React from 'react';
import cn from 'classnames';
import { NavLink } from 'react-router-dom';
import { MenuItem } from '../';
import CollapseExpand from '../CollapseExpand';

export interface PulsarMenuBarOptionProps {
  option: MenuItem;
  optionIndex: number;
  isMenuCollapse?: boolean;
  isExpandedOption: boolean;
  setExpandIndex: (index: number | null) => void;
  onOptionSelect?: (
    id: any,
    optionIndex?: number,
    subOptionIndex?: number
  ) => void;
  classNames?: string;
  selectedOptionId?: string;
  isNewText?: string;
  onOptionExpanded: (index: number) => void;
  onClick?: () => void;
  ['data-testid']?: string;
}

function PulsarMenuBarOption({
  option,
  optionIndex,
  isMenuCollapse,
  isExpandedOption,
  selectedOptionId,
  onOptionSelect,
  setExpandIndex,
  onOptionExpanded,
  onClick,
  'data-testid': dataTestId
}: PulsarMenuBarOptionProps) {
  const OptionWrapper = ({
    to,
    externalLink,
    children,
    className,
    onClick,
    activeClassName,
    'data-testid': testId
  }: any) => {
    if (externalLink) {
      return (
        <a
          href={to}
          onClick={(event) => {
            if (onClick) {
              onClick(event);
              event.stopPropagation();
            }
          }}
          className={className}
          target="_blank"
          rel="noreferrer"
          data-testid={testId}
        >
          {children}
        </a>
      );
    }

    return to ? (
      <NavLink
        to={to}
        className={({ isActive }) =>
          [className, isActive ? activeClassName : null]
            .filter(Boolean)
            .join(' ')
        }
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {children}
      </NavLink>
    ) : (
      <div className={cn(className)} onClick={onClick} data-testid={testId}>
        {children}
      </div>
    );
  };

  return (
    <>
      <OptionWrapper
        to={option.to}
        externalLink={option.externalLink}
        className={cn(
          'tw-cursor-pointer tw-flex tw-items-center tw-relative tw-px-2 tw-py-1.5 tw-gap-3 tw-self-stretch hover:tw-bg-[#F3F4F6] hover:tw-rounded',
          selectedOptionId === option.id && 'tw-bg-[#fbfbfb] tw-rounded'
        )}
        isMenuCollapse={isMenuCollapse}
        activeClassName={'tw-bg-[#fbfbfb] tw-rounded'}
        data-testid={dataTestId}
        onClick={() => {
          if (onClick) {
            onClick();
          }
          if (option.options && option.options.length > 0) {
            setExpandIndex(isExpandedOption ? null : optionIndex);
            if (!isExpandedOption) {
              onOptionExpanded(optionIndex);
            }
            return;
          }

          if (
            onOptionSelect &&
            (!option.options || option.options.length === 0)
          ) {
            onOptionSelect!(option.id, optionIndex, undefined);
          }
          if (option.onSelected) {
            option.onSelected();
          }
        }}
      >
        <div className={cn('tw-flex tw-gap-4 tw-items-center tw-w-full')}>
          <div className={'tw-h-[24px] tw-w-[24px] tw-flex tw-items-center'}>
            {option.icon}
          </div>
          {!isMenuCollapse && (
            <div className="!tw-gap-3 tw-flex tw-w-full tw-items-center">
              <div
                className={
                  'tw-font-sans tw-text-sm tw-leading-4 tw-font-normal tw-text-body tw-tracking-[0.7px]'
                }
              >
                {option.label}
              </div>
              {option.badge && (
                <div className="tw-ml-auto tw-flex tw-items-center">
                  {option.badge}
                </div>
              )}
              {option.options && option.options.length > 0 && (
                <CollapseExpand
                  onClick={() => {}}
                  isCollapse={!isExpandedOption}
                  className={'tw-ml-auto'}
                />
              )}
            </div>
          )}
        </div>
      </OptionWrapper>
      {!isMenuCollapse && option.options && option.options.length > 0 && (
        <div
          className={cn(
            'tw-transition-all tw-duration-300 tw-ease-in-out tw-border-l-2 tw-border-gray-200 tw-ml-4',
            isExpandedOption
              ? 'tw-h-auto tw-max-h-[300px] tw-opacity-100 tw-overflow-y-auto'
              : 'tw-h-0 tw-opacity-0 tw-overflow-hidden'
          )}
          key={`sub-option-list-${option.id}`}
        >
          <div className="tw-flex tw-flex-col tw-gap-1 tw-py-1 ">
            {option.options?.map((subOption, subOptionIndex) => (
              <OptionWrapper
                key={`sub-option-${subOption.id}`}
                to={subOption.to}
                externalLink={subOption.externalLink}
                className={cn(
                  'tw-rounded tw-flex tw-relative tw-h-10 tw-items-center tw-pl-2',
                  'tw-ml-2 tw-border-b-0 tw-h-8',
                  selectedOptionId === subOption.id && 'tw-bg-[#fbfbfb]',
                  'hover:tw-bg-[#F3F4F6] tw-rounded'
                )}
                data-testid={`menu-option-${subOption.id}`}
                onClick={() => {
                  if (onClick) {
                    onClick();
                  }
                  if (onOptionSelect) {
                    onOptionSelect!(subOption.id, optionIndex, subOptionIndex);
                  }
                  if (subOption.onSelected) {
                    subOption.onSelected();
                  }
                }}
                activeClassName={'tw-bg-[#fbfbfb]'}
              >
                <div
                  className={cn(
                    'tw-font-sans tw-text-sm tw-font-normal tw-tracking-[0.7px] tw-leading-4 tw-text-body tw-cursor-pointer tw-w-full'
                  )}
                >
                  {subOption.label}
                </div>
              </OptionWrapper>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default PulsarMenuBarOption;
